/**
 * i18next setup (WP1.10). Import once, before <App/> renders (see main.tsx).
 *
 * - Resources: every `locales/<code>/<namespace>.json` is bundled; only codes
 *   enabled in `config.ts` are offered.
 * - Detection: saved user choice (localStorage) → browser languages → "en".
 * - <html lang> and <html dir> follow the active language (dir is ready for Arabic).
 * - Dev only: `?lng=en-XA` loads the pseudo-locale (see pseudo.ts).
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LOCALE,
  LANGUAGE_STORAGE_KEY,
  localeDir,
  NAMESPACES,
  PSEUDO_LOCALE,
  SUPPORTED_LOCALES,
} from "./config";
import { pseudoResources, type ResourceTree } from "./pseudo";

type Resources = Record<string, Record<string, ResourceTree>>;

function loadResources(): Resources {
  const files = import.meta.glob<ResourceTree>("./locales/*/*.json", { eager: true, import: "default" });
  const out: Resources = {};
  for (const [path, tree] of Object.entries(files)) {
    const match = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (!match) continue;
    const [, code, ns] = match;
    if (!SUPPORTED_LOCALES.includes(code)) continue;
    out[code] = { ...out[code], [ns]: tree };
  }
  return out;
}

const resources = loadResources();
const devPseudo = import.meta.env.DEV;
if (devPseudo && resources[DEFAULT_LOCALE]) {
  resources[PSEUDO_LOCALE] = pseudoResources(resources[DEFAULT_LOCALE]);
}
const available = devPseudo ? [...SUPPORTED_LOCALES, PSEUDO_LOCALE] : [...SUPPORTED_LOCALES];

function readSaved(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Saved choice → browser languages (exact, then base language) → default. */
export function detectLocale(): string {
  // Guarded like `navigator` below: unit tests import this without a DOM.
  if (devPseudo && typeof window !== "undefined") {
    const fromQuery = new URLSearchParams(window.location.search).get("lng");
    if (fromQuery && available.includes(fromQuery)) return fromQuery;
  }
  const saved = readSaved();
  if (saved && available.includes(saved)) return saved;
  const browser = typeof navigator !== "undefined" ? (navigator.languages ?? [navigator.language]) : [];
  for (const tag of browser) {
    if (!tag) continue;
    if (SUPPORTED_LOCALES.includes(tag)) return tag;
    const base = tag.split("-")[0];
    if (SUPPORTED_LOCALES.includes(base)) return base;
  }
  return DEFAULT_LOCALE;
}

function applyDocumentLanguage(lng: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng;
  document.documentElement.dir = localeDir(lng);
}

i18n.on("languageChanged", applyDocumentLanguage);

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: available,
  ns: [...NAMESPACES],
  defaultNS: "common",
  interpolation: { escapeValue: false }, // React already escapes.
  returnNull: false,
  react: { useSuspense: false },
});

/**
 * Switch the interface language and remember the choice. This is what a
 * language switcher calls; unsupported codes are ignored.
 */
export async function setLocale(code: string): Promise<void> {
  if (!available.includes(code)) return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // Private mode / blocked storage: the choice just won't persist.
  }
  await i18n.changeLanguage(code);
}

/** The active locale for Intl formatting (see format.ts). */
export function activeLocale(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
}

export default i18n;
